import 'date-fns';
import React, { ReactNode, useState } from 'react';
import { map, find, includes } from 'lodash-es';
import { RegisterOptions } from 'react-hook-form/dist/types';
import { useFormContext } from 'react-hook-form';
import cx from 'classnames';

import Grid, { GridSize } from '@material-ui/core/Grid';
import Switch from '@material-ui/core/Switch';
import TextField from '@material-ui/core/TextField';
import MenuItem from '@material-ui/core/MenuItem';
import Divider from '@material-ui/core/Divider';
import { StyledComponentProps } from '@material-ui/core/styles';

import { useWindowSize } from '../../../custom-hooks';

import { DropDownItems } from '../../controls/dropdowns/dropdown';
import { AcceptableValue } from './editable-user-data';
import { useTrans } from '@/custom-hooks/useTrans';

// COMBO BOX TO BE FIXED
// import ComboBox from '../../controls/combobox/combobox';

import CheckBox from '../../controls/checkbox/checkbox';
import CountryRegionPostalCode from '../../molecules/country-region-postalcode/country-region-postalcode';
import DatePicker from '../../../../src/components/controls/date-picker/date-picker';
import DivButton from '../legos/div-button/div-button';
import FactoryDateRangePicker from './legos/factory-date-range-picker';
import FieldErrors from '../../molecules/field-errors/field-errors';
import FieldOverlay from '../../atoms/field-overlay/field-overlay';
import ImageUploader from '../../controls/image-uploader/image-uploader';
import PasswordStrength from '../../authentication/legos/password-strength';
import TextFieldDropdown from '../../molecules/text-field-dropdown/text-field-dropdown';

import styles from './field-factory.module.scss';

export enum FactoryTextFields {
    MultiLineTextField = 'MultiLineTextField',
    ReadOnlyTextField = 'ReadOnlyTextField',
    TextField = 'TextField',
    TextFieldPassword = 'TextFieldPassword',
    TextFieldPasswordWithStrength = 'TextFieldPasswordWithStrength',
}

export enum FactorySpacers {
    LineSpacer = 'LineSpacer',
    SimpleSpacer = 'SimpleSpacer',
    Spacer = 'Spacer',
}

export enum FactoryFields {
    CheckBox = 'CheckBox',
    ComboBox = 'ComboBox',
    CountryRegionPostalCode = 'CountryRegionPostalCode',
    DatePicker = 'DatePicker',
    DateRangePicker = 'DateRangePicker',
    ImageUploader = 'ImageUploader',
    SimpleDiv = 'SimpleDiv',
    SwitchToggle = 'SwitchToggle',
    TextFieldDropDown = 'TextFieldDropDown',
}

export type FactoryTextFieldTypes = keyof typeof FactoryTextFields;

export type GeneratorFieldType = FactorySpacers | FactoryFields | FactoryTextFieldTypes;

type ValueType = string | boolean | DropDownItems | any;
export type EditableFieldProps = {
    id: string;
    ids?: string[];
    fieldType?: GeneratorFieldType;
    overlay?: JSX.Element;
    fieldConfig?: any;
    value?: ValueType;
    values?: any; // This should be going away with HUB-1278
    restrictionRegex?: RegExp;
    optionList?: DropDownItems;
    onChange?: (id: string, val?: AcceptableValue) => void;
    label?: string | ReactNode;
    className?: string;
    fieldWidth?: GridSize;
    conditionalVisibility?: (data?: any) => boolean;
    conditionalValidation?: (data?: any) => any;
    partnerField?: string;
    validation?: RegisterOptions;
    fieldOnChange?: (newValue?: ValueType) => void;
    parentOnChange?: (newValue?: string) => any;
    disabled?: boolean;
    hidden?: boolean;
    InputProps?: StyledComponentProps<any>;
};
export interface FieldGeneratorProps {
    dictionaryBase?: string;
    label?: string;
    generatorFields: EditableFieldProps[];
    onChange?: (id: string, val: AcceptableValue) => void;
}
export function EditableFieldGenerator(props: FieldGeneratorProps) {
    const [passwdVisible, setPasswdVisible] = useState(false);

    const { t } = useTrans();
    const {
        register,
        formState: { errors },
    } = useFormContext();
    const {
        dictionaryBase,
        generatorFields,
        onChange: onChangeDefault = () => {
            console.warn('NO ONCHANGE FUNCTION SPECIFIED\nYou probably want to fix this');
        },
    } = props;
    const { isMobile } = useWindowSize();

    return (
        <Grid container alignItems='center' justifyContent='flex-start' spacing={2}>
            {map(generatorFields, (field) => {
                const {
                    id,
                    value,

                    // Complexe data structure for DateRangePicker
                    // Please remove this
                    // HUB-1278
                    values,

                    fieldType,
                    overlay,
                    fieldConfig,
                    optionList = [],
                    onChange: onChangeOverride,
                    fieldOnChange,
                    label,
                    className: fieldClass,
                    fieldWidth = 12,
                    conditionalVisibility,
                    conditionalValidation,
                    partnerField,
                    validation: defaultValidation,
                    restrictionRegex = /[<>"\\]/g,
                    disabled = false,
                    InputProps,
                } = field;

                const fieldClassName = cx(styles.baseStyle, fieldClass, styles.gridPosition);

                const needsNoLabel = fieldType === 'SwitchToggle';

                const fieldKey = `GeneratedEditableField|${dictionaryBase}|${id}`;
                const gridColumnSpan = isMobile ? 12 : fieldWidth;

                const onChange = onChangeOverride || onChangeDefault;
                const formLabelBase = `${dictionaryBase}.formLabels`;
                const fieldLabel = label ?? (t(`${formLabelBase}.${id}.label`) as string);
                const fieldPlaceholder = needsNoLabel
                    ? ''
                    : t(`${formLabelBase}.${id}.placeHolder`);

                const partner = find(generatorFields, (gf) => gf.id === partnerField);
                if (conditionalVisibility) {
                    if (conditionalVisibility(partner?.value)) return null;
                }

                const validation =
                    (conditionalValidation && conditionalValidation(partner?.value)) ||
                    defaultValidation;

                if (fieldType === 'LineSpacer') {
                    return (
                        <Grid key={fieldKey} item xs={gridColumnSpan}>
                            <Divider variant='middle' />
                        </Grid>
                    );
                }

                if (fieldType === 'SimpleSpacer') {
                    return (
                        <Grid key={fieldKey} item xs={gridColumnSpan}>
                            <div style={{ height: '1rem' }} />
                        </Grid>
                    );
                }

                if (fieldType === 'Spacer') {
                    return <Grid key={fieldKey} item xs={gridColumnSpan} />;
                }

                if (fieldType === 'SimpleDiv') {
                    return (
                        <Grid key={fieldKey} item xs={gridColumnSpan} className={fieldClassName}>
                            <span>{label || value || fieldLabel}</span>
                            <FieldOverlay>{overlay}</FieldOverlay>
                        </Grid>
                    );
                }

                if (fieldType === 'ImageUploader') {
                    const { aspectRatio = '16/9', allowCrop = true } = fieldConfig;

                    return (
                        <Grid key={fieldKey} item xs={gridColumnSpan} className={fieldClassName}>
                            <ImageUploader
                                value={value}
                                allowCrop={allowCrop}
                                aspectRatio={aspectRatio}
                                onChange={(newData: AcceptableValue | null) => {
                                    onChange(id, newData);
                                }}
                            />
                        </Grid>
                    );
                }

                if (includes(FactoryTextFields, fieldType)) {
                    const multiline = fieldType === 'MultiLineTextField';
                    const readOnly = fieldType === 'ReadOnlyTextField';
                    const isPassword =
                        fieldType === FactoryTextFields.TextFieldPassword ||
                        fieldType === FactoryTextFields.TextFieldPasswordWithStrength;
                    const showStrength =
                        fieldType === FactoryTextFields.TextFieldPasswordWithStrength;
                    const textFieldType = isPassword && !passwdVisible ? 'password' : 'text';

                    const passwordOrnament = isPassword
                        ? {
                              startAdornment: (
                                  <i
                                      className={`material-icons-outlined ${styles.textFieldOrnamentStart}`}
                                  >
                                      lock
                                  </i>
                              ),
                              endAdornment: (
                                  <DivButton
                                      onClick={() => setPasswdVisible(!passwdVisible)}
                                      className={styles.ornamentButton}
                                  >
                                      <i
                                          className={`material-icons-outlined ${styles.textFieldOrnamentEnd}`}
                                      >
                                          {passwdVisible ? 'visibility' : 'visibility_off'}
                                      </i>
                                  </DivButton>
                              ),
                          }
                        : null;

                    return (
                        <Grid key={fieldKey} item xs={gridColumnSpan}>
                            <TextField
                                inputProps={
                                    multiline
                                        ? { style: { maxHeight: '40vh', overflow: 'auto' } }
                                        : {}
                                }
                                className={fieldClassName}
                                key={`${dictionaryBase}|${id}`}
                                variant={readOnly ? 'standard' : 'outlined'}
                                label={fieldLabel}
                                type={textFieldType}
                                InputProps={{ ...InputProps, ...passwordOrnament }}
                                placeholder={fieldPlaceholder}
                                disabled={readOnly}
                                fullWidth
                                value={value}
                                multiline={multiline}
                                minRows={multiline ? 3 : 1}
                                {...register(id, {
                                    ...validation,
                                    onChange: (event) => {
                                        const newValue = restrictionRegex
                                            ? event.target.value.replace(restrictionRegex, '')
                                            : event.target.value;
                                        const noSpaceFirst = newValue === ' ' ? '' : newValue;
                                        onChange(id, noSpaceFirst);
                                    },
                                    onBlur: (e) => onChange(id, e.target.value.trim()),
                                })}
                            />
                            {showStrength ? <PasswordStrength password={value as string} /> : null}
                            <FieldErrors fieldId={id} errors={errors} />
                            <FieldOverlay>{overlay}</FieldOverlay>
                        </Grid>
                    );
                }

                if (fieldType === 'TextFieldDropDown') {
                    interface ListItemWithKey {
                        key: string | undefined;
                        value: string | undefined;
                    }

                    if (!optionList || optionList.length === 0) {
                        return (
                            <Grid key={fieldKey} item xs={gridColumnSpan}>
                                <div />
                            </Grid>
                        );
                    }

                    if (validation?.deps) {
                        // ToDo This is sketch
                        fieldOnChange && fieldOnChange(value as string);
                    }
                    const isValidValue = find(
                        optionList,
                        (opt: ListItemWithKey) => opt.key === value
                    );
                    const buildOptionList = () => {
                        return [
                            <MenuItem key='item0' value=''>
                                {t('labels.select', { AK_TAG_THING_TO_SELECT: fieldLabel })}
                            </MenuItem>,
                            ...map(optionList, (item: ListItemWithKey) => {
                                return (
                                    <MenuItem key={item.key} value={item.key}>
                                        {item.value}
                                    </MenuItem>
                                );
                            }),
                        ];
                    };

                    return (
                        <Grid key={fieldKey} item xs={gridColumnSpan}>
                            <TextFieldDropdown
                                id={id}
                                errors={errors}
                                overlay={overlay}
                                value={isValidValue ? value : ''}
                                label={fieldLabel as string}
                                textFieldOpts={register(id, {
                                    ...validation,
                                    onChange: (event) => {
                                        onChange(id, event.target.value);
                                        fieldOnChange && fieldOnChange(event.target.value);
                                    },
                                })}
                                helperText={errors.currency?.message as string}
                            >
                                {buildOptionList()}
                            </TextFieldDropdown>
                        </Grid>
                    );
                }

                if (fieldType === 'DatePicker') {
                    const { format, clearable = false } = fieldConfig;

                    return (
                        <Grid key={fieldKey} item xs={gridColumnSpan}>
                            <DatePicker
                                format={format}
                                label={fieldLabel as string}
                                initDate={field.value as string}
                                clearable={clearable}
                                onChange={(newDate: AcceptableValue) => {
                                    onChange(id, newDate?.toString().split('T')[0]);
                                }}
                            />
                            <FieldOverlay>{overlay}</FieldOverlay>
                        </Grid>
                    );
                }

                if (fieldType === 'DateRangePicker') {
                    // The <Grid /> element is added inside <FactoryDateRangePicker />
                    return (
                        <FactoryDateRangePicker
                            fieldConfig={fieldConfig}
                            key={fieldKey}
                            id={id}
                            dictionaryBase={dictionaryBase}
                            fieldKey={fieldKey}
                            values={values}
                            gridColumnSpan={gridColumnSpan}
                            onChangeOverride={onChangeDefault}
                        />
                    );
                }

                if (fieldType === 'ComboBox') {
                    return (
                        <Grid key={fieldKey} item xs={gridColumnSpan}>
                            COMBO BOX TO BE FIXED
                            {/*<ComboBox*/}
                            {/*    label={fieldLabel}*/}
                            {/*    value={value}*/}
                            {/*    onChange={(newData: AcceptableValue) => {*/}
                            {/*        onChange(id, newData);*/}
                            {/*    }}*/}
                            {/*    optionList={optionList}*/}
                            {/*/>*/}
                        </Grid>
                    );
                }

                if (fieldType === 'CheckBox') {
                    return (
                        <Grid key={fieldKey} item xs={gridColumnSpan}>
                            <CheckBox
                                checked={!!value}
                                label={fieldLabel}
                                onClick={() => onChange(id, !value)}
                            />
                        </Grid>
                    );
                }

                if (fieldType === 'SwitchToggle') {
                    return (
                        <Grid key={fieldKey} item xs={gridColumnSpan} className={fieldClassName}>
                            <Switch
                                disabled={disabled}
                                checked={!!value || false}
                                color='primary'
                                onChange={(newData: AcceptableValue) => {
                                    onChange(id, newData);
                                }}
                            />
                            <div style={{ width: '100%' }}>{fieldLabel}</div>
                            <FieldOverlay>{overlay}</FieldOverlay>
                        </Grid>
                    );
                }

                if (fieldType === 'CountryRegionPostalCode') {
                    const swapLastIDSegment = (fullID: string, newSegment: string) => {
                        const segments = fullID.split('-');
                        return [...segments.slice(0, -1), newSegment].join('-');
                    };

                    return (
                        <CountryRegionPostalCode
                            key={fieldKey}
                            dictionaryBase={dictionaryBase || ''}
                            register={register}
                            errors={errors}
                            validation={validation}
                            gridColumnSpan={gridColumnSpan}
                            onChange={(idSuffix: string, value: any) => {
                                onChange(swapLastIDSegment(id, idSuffix), value);
                            }}
                        />
                    );
                }

                return (
                    <Grid
                        key={fieldKey}
                        item
                        xs={gridColumnSpan}
                        className={styles.unsupportedFieldType}
                    >
                        <div>{`Cannot render ${id}`}</div>
                        <div>{`Currently unsupported fieldType : ${fieldType}`}</div>
                    </Grid>
                );
            })}
        </Grid>
    );
}
